import { useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../../../backend/firebase';
import firebaseBackend from '../../../backend/firebase';
import { useAppDispatch } from '../../hooks';
import { setUid, setAssignedColor, selectAnonymousTileIds, clearAnonymousTileIds } from './slice';
import { store, persistor } from '../../store';
import { track } from '../../../helpers/analytics';
import { Color } from '../color/color';
import TilesProvider from '../firestore/tiles/TilesProvider';
import { COLLECTIONS } from '@painttheworld/shared';

const AuthProvider = () => {
    const dispatch = useAppDispatch();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            // Guard: wait for redux-persist to finish rehydrating before reading
            // persisted state.
            if (!persistor.getState().bootstrapped) {
                await new Promise<void>(resolve => {
                    const timer = setTimeout(resolve, 3000);
                    const unsubPersist = persistor.subscribe(() => {
                        if (persistor.getState().bootstrapped) {
                            clearTimeout(timer);
                            unsubPersist();
                            resolve();
                        }
                    });
                });
            }

            if (user) {
                dispatch(setUid(user.uid));
                if (!user.isAnonymous) {
                    track('sign_in_completed', { method: 'google' });
                }

                const anonymousTileIds = selectAnonymousTileIds(store.getState());

                try {
                    const userDocRef = doc(db, COLLECTIONS.USERS, user.uid);
                    const userSnap = await getDoc(userDocRef);

                    let assignedColor: string;
                    let assignedColorRgb: Color;

                    if (userSnap.exists()) {
                        // Existing user — use stored color
                        const profile = userSnap.data();
                        assignedColor = profile.assignedColor;
                        assignedColorRgb = profile.assignedColorRgb;
                    } else {
                        // New user — call Cloud Function to assign color
                        const assignFn = httpsCallable<unknown, { assignedColor: string; assignedColorRgb: Color }>(
                            getFunctions(firebaseBackend), 'assignUserColor'
                        );
                        const { data } = await assignFn();
                        assignedColor = data.assignedColor;
                        assignedColorRgb = data.assignedColorRgb;
                    }

                    dispatch(setAssignedColor({ color: assignedColor, rgb: assignedColorRgb }));

                    // Upgrade anonymous tiles to the authenticated user
                    if (anonymousTileIds.length > 0) {
                        await TilesProvider.instance.upgradeTiles(anonymousTileIds, user.uid, assignedColorRgb);
                        dispatch(clearAnonymousTileIds());
                    }
                } catch (err) {
                    console.error('Failed to fetch/assign user color:', err);
                }
            } else {
                dispatch(setUid(null));
            }
        });
        return unsubscribe;
    }, [dispatch]);

    return null;
};

export default AuthProvider;
